/**
 * @file components/audit-tasks/phases/PhaseField.tsx
 * @description 階段 3：現場稽核。
 *
 * 內容（Tabs，4 個，「打卡」置於首位）：
 *  - 打卡：行內渲染 <CheckInOutSection />（到場 / 離場 + GPS）與 <FieldPhotoSection />
 *    （現場拍照存證），現場到場第一步，只做簽到 + 拍照，不夾帶 NC。
 *  - 查核表：列出綁定的 form_instance，點擊進入填寫頁；每列提供「下載 PDF / 列印」
 *    （客戶 PPT S49：有 WiFi 時現場可把填寫好的表單導出 PDF 或直接列印；
 *    離線時兩按鈕禁用並以 Tooltip 提示恢復網絡後操作）
 *  - 採樣送檢：行內渲染 <SamplesPanel />（沿用 case 為單位的採樣送檢登記，
 *    分「採樣單/送檢單」概念 + 採樣位置手繪示意圖，PPT S56/S57）
 *  - NC 紀錄：行內渲染 <FindingsPanel />（沿用 case 為單位的 NC 開立）
 *  - 稽核文件包內另含「Z附件」：稽核員可一次上傳多份現場補充證據，後續隨案件附件
 *    一併凍結進驗證決定 dossier_snapshot。
 *
 * 「查核表」Tab 完整實現於 Task #10（用 packages/forms Renderer 渲染）。
 * 拍照流統一由現場稽核頁面（field 模式）處理；本 phase 不再保留獨立 tab。
 */

import { useState } from 'react';
import { Alert, Button, Empty, Space, Table, Tabs, Tag, Tooltip, Typography } from 'antd';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import {
  ClipboardCheck,
  FlaskConical,
  AlertTriangle,
  MapPin,
  Download,
  Printer,
} from 'lucide-react';
import type { AuditTaskDetail, TaskFormInstanceSummary } from '@zhcb/shared';
import { message } from '@/lib/antd-app';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useFieldWriteAccess } from '@/hooks/useFieldWriteAccess';
import { SamplesPanel } from '@/components/audit-tasks/field/SamplesPanel';
import { FindingsPanel } from '@/components/audit-tasks/field/FindingsPanel';
import { CheckInOutSection } from '@/components/audit-tasks/field/CheckInOutSection';
import { FieldPhotoSection } from '@/components/audit-tasks/field/FieldPhotoSection';
import { AuditEvidenceAttachmentsCard } from '@/components/audit-tasks/field/AuditEvidenceAttachmentsCard';
import { fetchFormPdfBlob, printPdfBlob, triggerPdfDownload } from '@/lib/form-export';
import type { FieldTab } from '@/components/audit-tasks/phases/field-tabs';

interface Props {
  task: AuditTaskDetail;
  /** 初始頁籤（URL `?tab=`；表單頁「返回任務」帶回來源頁籤，序21）。未帶時預設「打卡」 */
  initialTab?: FieldTab;
}

const FORM_STATUS_LABEL: Record<TaskFormInstanceSummary['status'], string> = {
  draft: '草稿',
  submitted: '已提交',
  locked: '已鎖定',
};

const FORM_STATUS_COLOR: Record<TaskFormInstanceSummary['status'], string> = {
  draft: 'default',
  submitted: 'processing',
  locked: 'success',
};

/** 導出操作類型：下載 PDF / 列印（用於區分同一列上兩個按鈕的 loading 態） */
type ExportAction = 'download' | 'print';

export function PhaseField({ task, initialTab }: Props) {
  /** TanStack Query client：打卡成功後 invalidate 任務詳情，刷新 arrivedAt/leftAt 顯示 */
  const qc = useQueryClient();

  /** 網絡狀態：離線時禁用「下載 PDF / 列印」（導出必須請求後端） */
  const { isOnline } = useNetworkStatus();

  /** 序61：管理組（manager）唯讀——無現場寫入能力時查核表入口改為「檢視」文案 */
  const { canFieldWrite } = useFieldWriteAccess();

  /** 正在導出的表單實例與操作類型（控制對應按鈕的 loading，避免重複點擊） */
  const [exporting, setExporting] = useState<{ id: number; action: ExportAction } | null>(null);

  /**
   * 導出處理：拉取 PDF blob 後按操作類型分發（下載 / 列印）。
   * 失敗時以 antd message 提示具體原因。
   */
  const handleExport = async (record: TaskFormInstanceSummary, action: ExportAction) => {
    if (exporting) return; // 已有導出進行中，忽略重複點擊
    setExporting({ id: record.id, action });
    try {
      const blob = await fetchFormPdfBlob(record.id);
      if (action === 'download') {
        // 任務編號優先用稽核計劃編號（PLAN-YYYY-NNNNNN），回落案件編號 / 任務 id
        const taskNo = task.planCode ?? task.caseCode ?? `TASK-${task.id}`;
        triggerPdfDownload(blob, `${record.templateName}-${taskNo}.pdf`);
        message.success('PDF 已開始下載');
      } else {
        printPdfBlob(blob, (err) => {
          console.error('[PhaseField] print form pdf failed', err);
          message.error('喚起列印失敗，請改用「下載 PDF」後手動列印');
        });
      }
    } catch (err) {
      console.error('[PhaseField] export form pdf failed', { instanceId: record.id, action, err });
      message.error(
        `${action === 'download' ? '下載' : '列印'}失敗：${err instanceof Error ? err.message : '未知錯誤'}`,
      );
    } finally {
      setExporting(null);
    }
  };

  const formColumns: ColumnsType<TaskFormInstanceSummary> = [
    {
      title: '表單編碼',
      dataIndex: 'templateCode',
      width: 180,
      render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>,
    },
    {
      title: '名稱',
      dataIndex: 'templateName',
      ellipsis: true,
    },
    {
      title: '狀態',
      dataIndex: 'status',
      width: 100,
      render: (s: TaskFormInstanceSummary['status']) => (
        <Tag color={FORM_STATUS_COLOR[s]}>{FORM_STATUS_LABEL[s]}</Tag>
      ),
    },
    {
      title: '更新時間',
      dataIndex: 'updatedAt',
      width: 150,
      render: (d: string) => new Date(d).toLocaleString('zh-TW'),
    },
    {
      title: '操作',
      width: 220,
      render: (_, record) => {
        /** 離線提示：導出需請求後端，離線時禁用並提示（PPT S49「有 WiFi 時」前提） */
        const offlineTip = isOnline ? undefined : '離線狀態無法導出，請恢復網絡後操作';
        return (
          <Space size={4}>
            <Link
              to="/audit-tasks/$taskId/form/$instanceId"
              params={{ taskId: String(task.id), instanceId: String(record.id) }}
              // 帶上來源階段 + 來源頁籤：表單頁「返回任務」據此回到現場稽核的「稽核文件包」頁籤，
              // 而非丟失上下文（PPT S48；序21 修正舊版只帶階段、返回後落在「打卡」頁籤）
              search={{ from: 'field', fromTab: 'forms' }}
              style={{ fontSize: 12 }}
            >
              {canFieldWrite ? '填寫' : '檢視'}
            </Link>
            <Tooltip title={offlineTip}>
              <Button
                type="link"
                size="small"
                style={{ fontSize: 12, padding: '0 4px' }}
                icon={<Download size={12} />}
                disabled={!isOnline}
                loading={exporting?.id === record.id && exporting.action === 'download'}
                onClick={() => void handleExport(record, 'download')}
              >
                下載 PDF
              </Button>
            </Tooltip>
            <Tooltip title={offlineTip}>
              <Button
                type="link"
                size="small"
                style={{ fontSize: 12, padding: '0 4px' }}
                icon={<Printer size={12} />}
                disabled={!isOnline}
                loading={exporting?.id === record.id && exporting.action === 'print'}
                onClick={() => void handleExport(record, 'print')}
              >
                列印
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Tabs
      defaultActiveKey={initialTab ?? 'gps'}
      items={[
        {
          key: 'gps',
          label: (
            <Space size={4}>
              <MapPin size={14} />
              <span>打卡</span>
            </Space>
          ),
          children: (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* 序：打卡僅保留有機 TOP；產銷履歷 TAP 不需打卡（仍保留現場拍照） */}
              {task.caseScheme === 'TAP' ? (
                <Alert
                  type="info"
                  showIcon
                  message="產銷履歷（TAP）案件無需打卡"
                  description="本階段僅保留現場拍照；有機（TOP）案件才需到場／離場打卡。"
                />
              ) : (
                <CheckInOutSection
                  taskId={task.id}
                  arrivedAt={task.arrivedAt}
                  leftAt={task.leftAt}
                  onChanged={() =>
                    void qc.invalidateQueries({ queryKey: ['audit-task-detail', task.id] })
                  }
                />
              )}
              <FieldPhotoSection taskId={task.id} />
            </Space>
          ),
        },
        {
          key: 'forms',
          label: (
            <Space size={4}>
              <ClipboardCheck size={14} />
              <span>稽核文件包 ({task.forms.length})</span>
            </Space>
          ),
          children: (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Typography.Title level={5}>查核表</Typography.Title>
                {task.forms.length === 0 ? (
                  <Empty
                    description={
                      <Space direction="vertical" size={4}>
                        <Typography.Text type="secondary">尚未綁定查核表</Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          請至上一階段「稽核計劃」勾選需要的表單
                        </Typography.Text>
                      </Space>
                    }
                  />
                ) : (
                  <Table<TaskFormInstanceSummary>
                    rowKey="id"
                    dataSource={task.forms}
                    columns={formColumns}
                    pagination={false}
                    size="small"
                  />
                )}
              </div>
              <AuditEvidenceAttachmentsCard caseId={task.caseId} canWrite={canFieldWrite} />
            </Space>
          ),
        },
        {
          key: 'sampling',
          label: (
            <Space size={4}>
              <FlaskConical size={14} />
              <span>採樣送檢</span>
            </Space>
          ),
          children: <SamplesPanel caseId={task.caseId} taskId={task.id} scheme={task.caseScheme} />,
        },
        {
          key: 'findings',
          label: (
            <Space size={4}>
              <AlertTriangle size={14} />
              <span>NC 紀錄</span>
            </Space>
          ),
          children: <FindingsPanel caseId={task.caseId} />,
        },
      ]}
    />
  );
}
